import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  postUploadDatosTasacion,
  deleteAllDatosTasacion,
} from "../../action";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FileUpload from "../../assets/FileUpload";
import Swal from "sweetalert2";

const SubirDatosTasacion = () => {
  const dispatch = useDispatch();

  const [esVenta, setEsVenta] = useState(true);
  const [file, setFile] = useState(null);
  const [ready, setReady] = useState(false);
  const [message, setMessage] = useState("");
  const [hasInteracted, setHasInteracted] = useState(false);

  const requestPostUploadDatosTasacion = useSelector(
    (state) => state.app.requestPostUploadDatosTasacion
  );
  const requestDeleteAllDatosTasacion = useSelector(
    (state) => state.app.requestDeleteAllDatosTasacion
  );
  const errorMessage = useSelector((state) => state.app.errorMessage);

  const handleTipoChange = (event) => {
    setEsVenta(event.target.value === "venta");
  };

  const handleDrop = (droppedFiles) => {
    if (!droppedFiles || droppedFiles.length === 0) {
      setReady(false);
      setMessage(
        "Archivo inválido. Solo se acepta formato .xlsx y archivos con un peso igual o menor a 5 MB"
      );
      setHasInteracted(true);
      setFile(null);
      return;
    }

    const selectedFile = droppedFiles[0];
    const fileName = selectedFile.name || "";
    const isXlsx = fileName.toLowerCase().endsWith(".xlsx");
    const isWithinSizeLimit = selectedFile.size <= 5000000;

    if (!isXlsx || !isWithinSizeLimit) {
      setReady(false);
      setMessage(
        "Archivo inválido. Solo se acepta formato .xlsx y archivos con un peso igual o menor a 5 MB"
      );
      setFile(null);
    } else {
      setReady(true);
      setMessage("Archivo válido");
      setFile(selectedFile);
    }
    setHasInteracted(true);
  };

  const handleDropRejected = () => {
    setReady(false);
    setMessage(
      "Archivo inválido. Solo se acepta formato .xlsx y archivos con un peso igual o menor a 5 MB"
    );
    setHasInteracted(true);
    setFile(null);
  };

  const handleSubmit = () => {
    if (ready && file) {
      let formData = new FormData();
      formData.append("file", file);
      dispatch(postUploadDatosTasacion(formData, esVenta ? "venta" : "arriendo"));
    }
  };

  const showConfirmationDialog = () => {
    const tipoStr = esVenta ? "Venta" : "Arriendo";
    Swal.fire({
      title: "Eliminar datos de colección",
      text: `Confirma que desea eliminar los datos de la colección Datos Tasación ${tipoStr}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      footer: "Atención! Este proceso puede tardar varios minutos",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteAllDatosTasacion(esVenta ? "venta" : "arriendo"));
      }
    });
  };

  useEffect(() => {
    if (requestPostUploadDatosTasacion === "SUCCESS") {
      Swal.fire({
        title: "Archivo cargado",
        text: `Se ha subido el archivo a la colección Datos Tasación ${!esVenta ? "Arriendo" : "Venta"
          } exitosamente`,
        icon: "success",
      });
    } else if (requestPostUploadDatosTasacion === "ERROR") {
      Swal.fire({
        title: "Error",
        text: errorMessage || "Ocurrió un error al subir el archivo",
        icon: "error",
      });
    }
  }, [requestPostUploadDatosTasacion, esVenta, errorMessage]);

  useEffect(() => {
    if (requestDeleteAllDatosTasacion === "SUCCESS") {
      Swal.fire({
        title: "Colección eliminada",
        text: "Se han eliminado los datos de la colección Datos Tasación exitosamente",
        icon: "success",
      });
    } else if (requestDeleteAllDatosTasacion === "ERROR") {
      Swal.fire({
        title: "Error",
        text: errorMessage || "Ocurrió un error al eliminar la colección",
        icon: "error",
      });
    }
  }, [requestDeleteAllDatosTasacion, errorMessage]);

  const isLoading =
    requestPostUploadDatosTasacion === "LOADING" ||
    requestDeleteAllDatosTasacion === "LOADING";

  return (
    <Box sx={{ p: 4, width: "100%", maxWidth: 1200, margin: "0 auto" }}>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: "#333" }}>
          Datos tasación
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                Tipo de operación
              </FormLabel>
              <RadioGroup
                row
                aria-label="esVenta"
                name="esVenta"
                value={esVenta ? "venta" : "arriendo"}
                onChange={handleTipoChange}
              >
                <FormControlLabel
                  value="venta"
                  control={<Radio sx={{ color: "#00a62b", "&.Mui-checked": { color: "#00a62b" } }} />}
                  label="Venta"
                />
                <FormControlLabel
                  value="arriendo"
                  control={<Radio sx={{ color: "#00a62b", "&.Mui-checked": { color: "#00a62b" } }} />}
                  label="Arriendo"
                />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#f8f9fa", borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "#555" }}>
                Requisitos del archivo:
              </Typography>
              <List dense disablePadding>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#00a62b" }} />
                  </ListItemIcon>
                  <ListItemText primary="Sólo se aceptan archivos con formato .xlsx" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#00a62b" }} />
                  </ListItemIcon>
                  <ListItemText primary="Archivos con tamaño inferior a 5 MB" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#00a62b" }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={`Cabeceras requeridas: Comuna, Precio, SuperficieTotal, SuperficieUtil, Dormitorios, TipoPropiedad, Estacionamientos, Barrio, Link, UF_m2${!esVenta ? ", PrecioUF" : ""
                      }`}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <FileUpload
              acceptedFiles={["application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]}
              onDrop={handleDrop}
              onDropRejected={handleDropRejected}
              sizeLimit={5000000}
              maxFiles={1}
              showFiles={true}
            />
          </Grid>

          {hasInteracted && (
            <Grid item xs={12}>
              <Alert severity={ready ? "success" : "error"} sx={{ mt: 1 }}>
                {message}
              </Alert>
            </Grid>
          )}

          {hasInteracted && (
            <Grid item xs={12}>
              <Alert severity="warning">
                ¡Atención! Este proceso puede tardar varios minutos.
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
              disabled={!ready || isLoading}
              onClick={handleSubmit}
              sx={{
                backgroundColor: "#00a62b",
                "&:hover": { backgroundColor: "#008a24" },
                px: 4,
                py: 1.2,
                fontWeight: 600,
              }}
            >
              {isLoading ? "Procesando..." : "Subir archivo"}
            </Button>

            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineIcon />}
              disabled={isLoading}
              onClick={showConfirmationDialog}
              sx={{ px: 3, py: 1.2, fontWeight: 600 }}
            >
              Eliminar datos de colección
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default SubirDatosTasacion;
